import React, { type JSX } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router";
import ConversationsView from "./ConversationsView";
import ConversationDetail from "./ConversationDetail";
import TurnDetail from "./TurnDetail";
import ActionDetail from "./ActionDetail";
import LLMRequestDetail from "./LLMRequestDetail";
import SettingsView from "./SettingsView";

const TAB_CLASS_BASE = "pb-2 px-1 text-sm font-medium border-b-2 transition-colors";
const TAB_CLASS_ACTIVE = "border-blue-600 text-blue-600";
const TAB_CLASS_IDLE =
  "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300";

/**
 * Label for the deepest segment of a nested admin route. Ordered most-specific
 * first so the first match wins, which keeps this a flat lookup rather than the
 * chain of mutually-exclusive `includes` tests it replaces.
 */
function breadcrumbLabel(pathname: string): string {
  if (pathname.includes("/llm-request/")) return "LLM Request";
  if (pathname.includes("/action/")) return "Action";
  if (pathname.includes("/turn/")) return "Turn";
  if (pathname.includes("/conversation/")) return "Conversation";
  return "";
}

function AdminTab({
  label,
  isActive,
  onSelect,
}: {
  label: string;
  isActive: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onSelect}
      className={`${TAB_CLASS_BASE} ${isActive ? TAB_CLASS_ACTIVE : TAB_CLASS_IDLE}`}
    >
      {label}
    </button>
  );
}

function AdminBreadcrumb({
  pathname,
  onHome,
}: {
  pathname: string;
  onHome: () => void;
}): JSX.Element {
  return (
    <div className="mt-2 flex items-center space-x-2 text-sm">
      <button onClick={onHome} className="text-blue-600 hover:text-blue-800 underline">
        Conversations
      </button>
      <span className="text-gray-400">/</span>
      <span className="text-gray-700">{breadcrumbLabel(pathname)}</span>
    </div>
  );
}

function AdminHeader(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const isNested = pathname !== "/admin" && pathname !== "/admin/settings";

  return (
    <div className="border-b border-gray-200 bg-gray-50 p-4 rounded-t-lg">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
        <div className="text-sm text-gray-600">Audit Data Explorer</div>
      </div>

      <div className="mt-4 flex items-center space-x-4 border-b border-gray-200">
        <AdminTab
          label="Conversations"
          isActive={pathname === "/admin" || pathname.includes("/conversation/")}
          onSelect={() => void navigate("/admin")}
        />
        <AdminTab
          label="Settings"
          isActive={pathname === "/admin/settings"}
          onSelect={() => void navigate("/admin/settings")}
        />
      </div>

      {isNested && (
        <AdminBreadcrumb pathname={pathname} onHome={() => void navigate("/admin")} />
      )}
    </div>
  );
}

export default function AdminLayout(): JSX.Element {
  return (
    <div className="bg-white rounded-lg shadow flex flex-col">
      <AdminHeader />

      <div className="p-4 min-h-0 flex-1 overflow-auto">
        <Routes>
          <Route index element={<ConversationsView />} />
          <Route path="settings" element={<SettingsView />} />
          <Route path="conversation/:conversationId" element={<ConversationDetail />} />
          <Route path="conversation/:conversationId/turn/:turnId" element={<TurnDetail />} />
          <Route
            path="conversation/:conversationId/turn/:turnId/action/:actionId"
            element={<ActionDetail />}
          />
          <Route
            path="conversation/:conversationId/turn/:turnId/action/:actionId/llm-request/:llmRequestId"
            element={<LLMRequestDetail />}
          />
        </Routes>
      </div>
    </div>
  );
}
